import { ChangeEvent, DetailedHTMLProps, forwardRef, HTMLAttributes, InputHTMLAttributes, memo, useEffect, useId, useMemo, useRef } from "react";
import classNames from "classnames";
import { RefType } from "../../../../types";
import { useMergedRef } from "../../../../utils";

import styles from "./style.module.scss";

export type ToggleBaseSize = "sm" | "md";


export type ToggleBaseProps = Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, "value" | "onChange" | "ref"> & {
  indeterminate?: boolean,
  value?: boolean,
  checked?: boolean,
  onChange?: (value: boolean, e: ChangeEvent<HTMLInputElement>) => void,
  toggleBaseSize?: ToggleBaseSize,
  containerProps?: DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>
  inputRef?: RefType<HTMLInputElement>,
  inputState?: unknown
  hintText?: unknown
}

export const ToggleBase = memo(forwardRef<HTMLSpanElement, ToggleBaseProps>((
  {
    id,
    indeterminate,
    value,
    checked,
    disabled,
    onChange,
    className,
    containerProps,
    inputRef,
    toggleBaseSize = "md",
    inputState,
    hintText,
    ...props
  },
  ref,
) => {
  const resolvedRef = useRef<HTMLInputElement>();
  const reactId = useId();
  const realId = id || reactId;
  const isChecked = (value === undefined ? (checked) : value) || false;
  const mergedInputRef = useMergedRef(resolvedRef, inputRef);

  const onValueChanged = useMemo(() => (e: ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange?.(e.target.checked, e);
    }
  }, [ disabled, onChange ]);

  useEffect(() => {
    if (resolvedRef.current) {
      (resolvedRef.current as any).indeterminate = indeterminate;
    }
  }, [ resolvedRef, indeterminate ]);

  return (
    <span
      {...containerProps}
      className={classNames(
        styles.root,
        className,
        styles[toggleBaseSize as keyof typeof styles],
      )}
      data-input-type="toggle"
      data-input-state={indeterminate ? "indeterminate" : isChecked}
      data-input-disabled={disabled}
      data-toggle-base-size={toggleBaseSize}
      ref={ref}
    >
      <input
        {...props}
        id={realId}
        ref={mergedInputRef}
        value={`${isChecked}`}
        checked={isChecked}
        disabled={disabled}
        onChange={onValueChanged}
        type="checkbox"
      />
      <span role="button" className={styles.icon}>
        <span className={styles.circle} />
      </span>
    </span>
  );
}));